<template>
  <div class="horizontal layout center-justified">
    <!-- Previous voting sticker -->
    <div
      class="stop-submission item-slide-tooltip"
      v-if="isBrainstormPreviousStickerVisible"
    >
      <button
        class="btn btn-outline-dark btn-sm"
        @click="handleIdeasToPreviousStep()"
      >
        <aha-icon name="arrow-no-tail" class="rotate-180 mb-0.5" /> {{ $t('previous') }}
      </button>
    </div>

    <!-- Next voting sticker -->
    <div
      class="voting-sticker item-slide-tooltip"
      v-if="isBrainstormNextStickerVisible"
    >
      <button
        class="btn btn-primary btn-sm"
        :disabled="hasNoIdeas"
        @click="hasNoIdeas ? false : handleIdeasToNextStep()"
        :class="{'disabled': hasNoIdeas}"
      >
        <span
          class="pr-1"
          v-if="isNextVotingBtnVisible"
        >{{ $t('next') }}: {{ $t('voting') }}</span>
        <span
          class="pr-1"
          v-if="isNextResultBtnVisible"
        >{{ $t('next') }}: {{ $t('result') }}</span>
        <aha-icon
          name="arrow-no-tail"
          class="mb-0.5"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as Sentry from '@sentry/vue'
import SlideApi from '@/api/slide'
import constants from '@/constant'
import utils from '@/util'

export default {
  data() {
    return {
      tabName: constants.IdeasTabName,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    hasNoIdeas() {
      return this.slideActive.SlideOptions.length === 0
    },
    isBrainstormPreviousStickerVisible() {
      return this.slideActive.votingStep && this.slideActive.votingStep !== this.tabName.SUBMISSION
    },
    isBrainstormNextStickerVisible() {
      return this.slideActive.votingStep && this.slideActive.votingStep !== this.tabName.RESULT
    },
    isSubmissionStep() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.SUBMISSION
    },
    isVotingStep() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.VOTING
    },
    isNextVotingBtnVisible() {
      return this.isSubmissionStep || !this.slideActive.votingStep
    },
    isNextResultBtnVisible() {
      return this.isVotingStep
    },
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    async handleIdeasToNextStep() {
      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      }
      this.updateSlideActiveAction({
        showQuestionImage: false,
      })

      if (this.slideActive.votingStep === this.tabName.SUBMISSION) {
        payload.votingStep = this.tabName.VOTING
        this._trackGAEventWhenNextStep(this.slideActive.votingStep, payload.votingStep)
        this.updateSlideActiveAction({
          votingStep: this.tabName.VOTING,
        })
      } else {
        payload.votingStep = this.tabName.RESULT
        this._trackGAEventWhenNextStep(this.slideActive.votingStep, payload.votingStep)
        this.updateSlideActiveAction({
          votingStep: this.tabName.RESULT,
        })
        this._handleScrollToTop()
      }
      this._trackGAWhenEnableAllSteps()
      this.setSyncLoadingAction(1)
      try {
        await SlideApi.patchSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, `Ideas: Update votingStep to '${payload.votingStep}' failed.`)
      }
    },

    async handleIdeasToPreviousStep() {
      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      }
      this.updateSlideActiveAction({
        showQuestionImage: false,
      })
      if (this.slideActive.votingStep === this.tabName.VOTING) {
        payload.votingStep = this.tabName.SUBMISSION
        this._trackGAEventWhenNextStep(this.slideActive.votingStep, payload.votingStep)
        this.updateSlideActiveAction({
          votingStep: this.tabName.SUBMISSION,
        })
      } else if (this.slideActive.votingStep === this.tabName.RESULT) {
        payload.votingStep = this.tabName.VOTING
        this._trackGAEventWhenNextStep(this.slideActive.votingStep, payload.votingStep)
        this.updateSlideActiveAction({
          votingStep: this.tabName.VOTING,
        })
      }
      this.setSyncLoadingAction(1)
      try {
        await SlideApi.patchSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, `Ideas: Update votingStep to '${payload.votingStep}' failed.`)
      }
    },

    _trackGAEventWhenNextStep(currentStep, nextStep) {
      utils.gaTrackEvent(
        constants.gaCategories.PRESENTATION_EDITOR,
        'Brainstorm > Change voting step',
        `From step: ${currentStep} to step: ${nextStep}`,
      )
    },

    _trackGAWhenEnableAllSteps() {
      utils.gaTrackEvent(
        constants.gaCategories.PRESENTATION_EDITOR,
        'Brainstorm > Change voting step > Enable all steps',
      )
    },

    _handleScrollToTop() {
      const container = this.$el.querySelector('#masonry-container')
      if (container && container.scrollTop) {
        container.scrollTop = 0
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.rotate-180 {
  transform: rotate(180deg);
}
.item-slide-tooltip {
  padding: 0 8px;
  border-radius: 50px;
  font-weight: bold;
  margin-right: 5px;
  font-size: 1rem;
  white-space: nowrap;
  margin-bottom: 3px;

  &:last-child {
    margin-right: 0;
  }

  .btn {
    font-weight: bold;
    font-size: 16px;
    padding: 2px 15px;
    margin: -3px 0 0 0;
    border: 1px solid rgba(0, 0, 0, 0.3);
  }

   &.stop-submission {
    .btn {
      background-color: #fff;

      &:hover {
        background-color: $primary-color;
        color: #ffffff;
      }
      &:focus {
        box-shadow: none;
      }
    }
  }
  &.voting-sticker {
    .btn {
      background-color: $primary-color;
      &:hover {
        background-color: $primary-hover-color;
      }
    }
    .btn:disabled{
      background-color: $primary-color;
      cursor: not-allowed;
    }
  }
}
</style>
